import { FunctionComponent, useCallback, useState } from 'react';
import { isEqual } from 'lodash';
import styled from 'styled-components';
import {
  HSBAColor,
  hsbToHex,
  ColorPicker as PolarisColorPicker,
  RGBColor,
  rgbToHsb,
  TextField,
  TextStyle,
} from '@shopify/polaris';
import Dropdown from '@components/ui/dropdown';
import { SpaceBetweenRowContainer } from '@components/ui/container/common';
import { defaultPaletteSize, getStoredColors } from '@utils/colors-storage';
import { HelpText } from '../common';

const TitleAndHelpText = styled.div`
  padding: 0 25px 0 0;
`;

const ColorPickerDropdown = styled(Dropdown)`
  .Polaris-Button {
    padding: 0;
    border: none;
    box-shadow: none;
    :hover {
      background-color: transparent;
      background-image: none;
    }
  }
`;

export const ColorDisplay = styled.div<{ color: string }>`
  width: 36px;
  height: 14px;
  border-radius: 2px;
  background: ${({ color }) => color};
`;

const ColorSwatch = styled.div`
  padding: 5px;
  background: #fff;
  border-radius: 1px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  display: inline-block;
  cursor: pointer;
`;

const ColorSelector = styled.div`
  max-width: 200px;
  width: 100%;
  .Polaris-Labelled__LabelWrapper {
    margin-top: 10px;
  }
`;

const ColorPalette = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 1rem 0 0 0;
`;

const Color = styled.div<{ color: string }>`
  width: 30px;
  height: 30px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  background-color: ${({ color }) => color};
  border-radius: 100%;
  cursor: pointer;
  margin: 0 1rem 1rem 0;
`;

const hexColorRegex = /^(?:[0-9a-fA-F]{3}){1,2}$/;

const convertHexToRgb = (hexColor: string): RGBColor | undefined => {
  const getNumber = (colors: string[]) => parseInt(colors.join(''), 16);
  const color = hexColor.replace('#', '').trim();
  if (hexColorRegex.test(color)) {
    // eslint-disable-next-line prefer-const
    let [R1, R2, G1, G2, B1, B2] = color.split('');
    if (color.length === 3) {
      const g = R2;
      const b = G1;
      R2 = R1;
      G1 = G2 = g;
      B1 = B2 = b;
    }
    return {
      blue: getNumber([B1, B2]),
      green: getNumber([G1, G2]),
      red: getNumber([R1, R2]),
    };
  }
};

export type ColorPickerPros = {
  color: string;
  disabled?: boolean;
  helpText?: string;
  label?: React.ReactElement | string;
  onChange(color: string): void;
  paletteSize?: number;
};

const ColorPicker: FunctionComponent<ColorPickerPros> = ({
  color,
  disabled,
  helpText,
  label,
  onChange,
  paletteSize = defaultPaletteSize,
}) => {
  const [hexColor, setHexColor] = useState(color);
  const [hsbColor, setHSBColor] = useState(
    rgbToHsb(
      convertHexToRgb(color) ?? {
        blue: 0,
        green: 0,
        red: 0,
      },
    ),
  );

  const onChangeColor = useCallback(
    (hexColor: string) => onChange(hexColor),
    [onChange],
  );

  const compareHex = (hex1: string, hex2: string) =>
    isEqual(convertHexToRgb(hex1), convertHexToRgb(hex2));

  const formatHex = (color: string) => {
    const colorFormatted = color.startsWith('#') ? color : `#${color}`;
    return colorFormatted.substring(0, 7);
  };

  const onChangeHsb = (hsbColor: HSBAColor) => {
    setHSBColor(hsbColor);

    const newHexColorFormatted = formatHex(hsbToHex(hsbColor));

    if (!compareHex(newHexColorFormatted, hexColor)) {
      setHexColor(newHexColorFormatted);
    }

    onChangeColor(newHexColorFormatted);
  };
  const onChangeHex = (hexColor: string) => {
    const newHexColorFormatted = formatHex(hexColor);
    setHexColor(newHexColorFormatted);

    const rgb = convertHexToRgb(newHexColorFormatted);
    if (rgb) {
      setHSBColor(rgbToHsb(rgb));
    }

    onChangeColor(newHexColorFormatted);
  };

  const isValid = convertHexToRgb(hexColor) !== undefined;
  const colorsPalette = getStoredColors().slice(0, paletteSize);
  const showPalette = colorsPalette && colorsPalette.length > 0;

  return (
    <SpaceBetweenRowContainer>
      {(label || helpText) && (
        <TitleAndHelpText>
          {label && <TextStyle>{label}</TextStyle>}
          {helpText && <HelpText marginTop="5px">{helpText}</HelpText>}
        </TitleAndHelpText>
      )}
      <ColorPickerDropdown
        activator={
          <ColorSwatch>
            <ColorDisplay color={hexColor} />
          </ColorSwatch>
        }
        disabled={disabled}
      >
        <ColorSelector>
          <PolarisColorPicker onChange={onChangeHsb} color={hsbColor} />
          {showPalette && (
            <ColorPalette>
              {colorsPalette.map((color) => (
                <Color
                  onClick={() => onChangeHex(color)}
                  key={color}
                  color={color}
                />
              ))}
            </ColorPalette>
          )}

          <TextField
            autoComplete="off"
            label="Hex"
            type="text"
            value={hexColor}
            onChange={onChangeHex}
            error={!isValid}
          />
        </ColorSelector>
      </ColorPickerDropdown>
    </SpaceBetweenRowContainer>
  );
};

export default ColorPicker;
